import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import {
  Scale, Plus, Trash2, FileText, Eye, Download, Printer, RotateCcw,
  PackageOpen, LogOut, Save, FolderOpen, Languages,
} from "lucide-react";

import Vakalatnama from "@/components/documents/Vakalatnama";
import AdjournmentApplication from "@/components/documents/AdjournmentApplication";
import AddressMemo from "@/components/documents/AddressMemo";
import VakalatnamaMarathi from "@/components/documents/marathi/VakalatnamaMarathi";
import AdjournmentApplicationMarathi from "@/components/documents/marathi/AdjournmentApplicationMarathi";
import AddressMemoMarathi from "@/components/documents/marathi/AddressMemoMarathi";
import DocumentPreviewDialog from "@/components/DocumentPreviewDialog";
import PaymentDownloadDialog from "@/components/PaymentDownloadDialog";
import { generatePdfFromElement } from "@/lib/pdfGenerator";
import { useAuth } from "@/context/AuthContext";
import { useMarathiEdit } from "@/context/MarathiEditContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ROLES = [
  "Appellant",
  "Respondent",
  "Applicant",
  "Opponent",
  "Petitioner",
  "Defendant",
  "Plaintiff",
  "Complainant",
  "Accused",
  "Revision Applicant",
  "Revision Opponent",
  "Other",
];

// Party 1 (initiating side) — only primary / legal-initiating roles.
const PARTY1_ROLES = [
  "Applicant",
  "Plaintiff",
  "Petitioner",
  "Appellant",
  "Complainant",
  "Claimant",
  "Revision Applicant",
  "Execution Applicant",
  "Decree Holder",
];

// Fixed English mapping: Party 1 role → auto-derived Party 2 role.
const PARTY1_TO_PARTY2 = {
  Applicant: "Opponent",
  Plaintiff: "Defendant",
  Petitioner: "Respondent",
  Appellant: "Respondent",
  Complainant: "Accused",
  Claimant: "Respondent",
  "Revision Applicant": "Revision Opponent",
  "Execution Applicant": "Judgment Debtor",
  "Decree Holder": "Judgment Debtor",
};

const todayIso = () => {
  const d = new Date();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${mm}-${dd}`;
};

const emptyForm = {
  // Case Details
  caseNo: "",
  courtName: "",
  courtPlace: "",
  date: todayIso(),

  // Party Details
  party1Name: "",
  party1Role: "Plaintiff",
  party2Name: "",
  party2Role: "Defendant",

  // Advocate appearing for
  advocateAppearingFor: "Plaintiff",
  advocateAppearingNumber: "1",

  // Main Advocate
  advocateName: "",
  advocateMahNumber: "",
  advocateCode: "",
  advocateAddress: "",
  advocateMobile: "",
  advocateEmail: "",

  // Extra advocates
  extraAdvocates: [],

  // Misc
  adjournmentReason: "",

  // Address memo entries (separate Name + Address, matches PDF's 2-column table)
  partyAddresses: [{ name: "", address: "" }],
};

export default function LegalFormApp() {
  const [form, setForm] = useState(emptyForm);
  const [preview, setPreview] = useState(null); // 'vakalatnama' | 'adjournment' | 'addressMemo'
  const [previewLang, setPreviewLang] = useState("en"); // 'en' | 'mr'
  const [generating, setGenerating] = useState(false);
  const [savedCases, setSavedCases] = useState([]);
  const [currentCaseId, setCurrentCaseId] = useState(null);
  const [showCases, setShowCases] = useState(false);

  const { user, logout } = useAuth();
  // Auto-derive Party No. 2 Role from Party No. 1 Role using the fixed
  // mapping. Runs whenever party1Role changes; keeps form.party2Role in
  // sync so both English and Marathi documents (previews + PDFs) render
  // the correct opposing-side term. If party1Role isn't in the mapping
  // (edge / migration case), party2Role stays as-is.
  useEffect(() => {
    const derived = PARTY1_TO_PARTY2[form.party1Role];
    if (derived && derived !== form.party2Role) {
      update({ party2Role: derived });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.party1Role]);

  const marathiCtx = useMarathiEdit();

  // ================= Marathi translation prefetch (perf: #1 + #2 + #3) =================
  // #2 Prefetch: runs whenever any dynamic form field changes, regardless
  //     of the currently-active preview language, so by the time the user
  //     clicks the Marathi tab the translations are already resolved.
  // #3 Debounce: coalesces bursty typing into a single 400ms-delayed batch
  //     to avoid dozens of small requests during input.
  // #1 Local deterministic month/digit → skipped from the LLM batch (see
  //     the role/month seeding effect below).
  const translateDebounceRef = useRef(null);
  useEffect(() => {
    if (!marathiCtx || !marathiCtx.ready) return;
    const appearLabel = form.advocateAppearingFor
      ? `${form.advocateAppearingFor}${
          form.advocateAppearingNumber && String(form.advocateAppearingNumber).trim()
            ? ` No. ${String(form.advocateAppearingNumber).trim()}`
            : ""
        }`
      : "";

    const values = [
      form.caseNo, form.courtName, form.courtPlace,
      form.party1Name, form.party2Name,
      form.advocateName, form.advocateAddress,
      form.adjournmentReason,
      appearLabel,
      ...(form.extraAdvocates || []).flatMap((a) => [a.name]),
      ...(form.partyAddresses || []).flatMap((p) => [p.name, p.address]),
    ];
    const uniq = Array.from(new Set(values.filter((v) => v && String(v).trim())));
    if (!uniq.length) return;

    // Debounce so rapid typing does not spam the endpoint.
    if (translateDebounceRef.current) clearTimeout(translateDebounceRef.current);
    translateDebounceRef.current = setTimeout(() => {
      marathiCtx.requestTranslations(uniq);
    }, 400);
    return () => {
      if (translateDebounceRef.current) clearTimeout(translateDebounceRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [marathiCtx?.ready, form.caseNo, form.courtName, form.courtPlace,
      form.party1Name, form.party2Name, form.advocateName, form.advocateAddress,
      form.adjournmentReason, form.advocateAppearingFor, form.advocateAppearingNumber,
      JSON.stringify(form.extraAdvocates), JSON.stringify(form.partyAddresses)]);

  // Deterministic English→Marathi mapping for the "Advocate Appearing For"
  // role dropdown. Whenever the user selects a role (with or without a
  // party number) we seed the marathi-override so <T source> renders the
  // exact term below in every Marathi document (Vakalatnama, Adjournment,
  // Address Memo). Overrides are persisted per-user via the existing
  // /api/marathi-overrides endpoint so the mapping also flows to the
  // downloaded Marathi PDF (same context, same overrides).
  useEffect(() => {
    if (!marathiCtx || !marathiCtx.ready) return;
    const ROLE_MAP = {
      Appellant: "अपीलकर्ता",
      Respondent: "रिस्पॉन्डंट",
      Applicant: "अर्जदार",
      Opponent: "वि. प.",
      Petitioner: "वादी",
      Defendant: "प्रतिवादी",
      Plaintiff: "फिर्यादी",
      Complainant: "फिर्यादी",
      Accused: "आरोपी",
      Other: "इतर",
      // New party-1 roles introduced with the auto-derived party 2:
      Claimant: "दावेदार",
      "Revision Applicant": "रिव्हिजन अर्जदार",
      "Execution Applicant": "अंमलबजावणी अर्जदार",
      "Decree Holder": "हुकूमधारक",
      // Auto-derived party-2 opposing terms:
      "Revision Opponent": "रिव्हिजन वि. प.",
      "Judgment Debtor": "निकाल ऋणी",
    };
    const toDev = (s) =>
      String(s).replace(/[0-9]/g, (d) => "०१२३४५६७८९"[Number(d)]);

    const MONTH_MAP = {
      January: "जानेवारी", February: "फेब्रुवारी", March: "मार्च",
      April: "एप्रिल", May: "मे", June: "जून",
      July: "जुलै", August: "ऑगस्ट", September: "सप्टेंबर",
      October: "ऑक्टोबर", November: "नोव्हेंबर", December: "डिसेंबर",
    };

    // Build a batch of any missing/mismatched seed keys.
    const batch = {};
    for (const [en, mr] of Object.entries(ROLE_MAP)) {
      const key = `trans:${en}`;
      if (marathiCtx.overrides?.[key] !== mr) batch[key] = mr;
    }
    for (const [en, mr] of Object.entries(MONTH_MAP)) {
      const key = `trans:${en}`;
      if (marathiCtx.overrides?.[key] !== mr) batch[key] = mr;
    }

    // Composite appearLabel ("Plaintiff No. 2" → "फिर्यादी क्र. २").
    const en = form.advocateAppearingFor;
    if (en && ROLE_MAP[en]) {
      const numStr = String(form.advocateAppearingNumber || "").trim();
      if (numStr) {
        const enLabel = `${en} No. ${numStr}`;
        const mrLabel = `${ROLE_MAP[en]} क्र. ${toDev(numStr)}`;
        const key = `trans:${enLabel}`;
        if (marathiCtx.overrides?.[key] !== mrLabel) batch[key] = mrLabel;
      }
    }

    if (Object.keys(batch).length) {
      marathiCtx.bulkUpdateOverrides(batch);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [marathiCtx?.ready, form.advocateAppearingFor, form.advocateAppearingNumber]);

  const vakRef = useRef(null);
  const adjRef = useRef(null);
  const memoRef = useRef(null);
  const vakMrRef = useRef(null);
  const adjMrRef = useRef(null);
  const memoMrRef = useRef(null);

  // Auto-load advocate profile on mount
  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const r = await fetch(`${BACKEND_URL}/api/profile`, { credentials: "include" });
        if (r.ok) {
          const p = await r.json();
          const hasAny = Object.values(p).some((v) => v && String(v).trim());
          if (hasAny) {
            setForm((prev) => ({ ...prev, ...p }));
          }
        }
      } catch (e) { /* ignore */ }
      loadCases();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const loadCases = async () => {
    try {
      const r = await fetch(`${BACKEND_URL}/api/cases`, { credentials: "include" });
      if (r.ok) setSavedCases(await r.json());
    } catch (e) { /* ignore */ }
  };

  const handleSaveProfile = async () => {
    try {
      const body = {
        advocateName: form.advocateName,
        advocateMahNumber: form.advocateMahNumber,
        advocateCode: form.advocateCode,
        advocateAddress: form.advocateAddress,
        advocateMobile: form.advocateMobile,
        advocateEmail: form.advocateEmail,
      };
      const r = await fetch(`${BACKEND_URL}/api/profile`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(body),
      });
      if (!r.ok) throw new Error();
      toast.success("Advocate profile saved to your account.");
    } catch (e) {
      toast.error("Could not save profile.");
    }
  };

  const handleSaveCase = async () => {
    if (!validate()) return;
    try {
      const payload = { title: form.caseNo || "Untitled", data: form };
      const url = currentCaseId
        ? `${BACKEND_URL}/api/cases/${currentCaseId}`
        : `${BACKEND_URL}/api/cases`;
      const method = currentCaseId ? "PUT" : "POST";
      const r = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload),
      });
      if (!r.ok) throw new Error();
      const saved = await r.json();
      setCurrentCaseId(saved.id);
      toast.success(currentCaseId ? "Case updated." : "Case saved.");
      loadCases();
    } catch (e) {
      toast.error("Could not save case.");
    }
  };

  const handleLoadCase = async (caseId) => {
    try {
      const r = await fetch(`${BACKEND_URL}/api/cases/${caseId}`, { credentials: "include" });
      if (!r.ok) throw new Error();
      const c = await r.json();
      setForm({ ...emptyForm, ...c.data });
      setCurrentCaseId(c.id);
      setShowCases(false);
      toast.success(`Loaded case: ${c.title}`);
    } catch (e) {
      toast.error("Could not load case.");
    }
  };

  const handleDeleteCase = async (caseId, e) => {
    e?.stopPropagation();
    if (!window.confirm("Delete this case?")) return;
    try {
      const r = await fetch(`${BACKEND_URL}/api/cases/${caseId}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (!r.ok) throw new Error();
      toast.success("Case deleted.");
      if (currentCaseId === caseId) setCurrentCaseId(null);
      loadCases();
    } catch (e) {
      toast.error("Could not delete case.");
    }
  };

  const update = (patch) => setForm((prev) => ({ ...prev, ...patch }));

  const validate = () => {
    const req = [
      ["caseNo", "Case Number"],
      ["courtName", "Court Name"],
      ["courtPlace", "Court Place"],
      ["party1Name", "Party No. 1 Name"],
      ["party2Name", "Party No. 2 Name"],
      ["advocateName", "Advocate Name"],
      ["advocateMahNumber", "MAH Number"],
      ["advocateCode", "Advocate Code"],
      ["advocateAddress", "Advocate Address"],
      ["advocateMobile", "Advocate Mobile"],
      ["advocateEmail", "Advocate Email"],
    ];
    for (const [k, label] of req) {
      if (!form[k] || !String(form[k]).trim()) {
        toast.error(`Please fill: ${label}`);
        return false;
      }
    }
    return true;
  };

  const handleAddExtraAdvocate = () =>
    update({
      extraAdvocates: [
        ...form.extraAdvocates,
        { name: "", mah: "", code: "" },
      ],
    });

  const handleRemoveExtraAdvocate = (idx) =>
    update({ extraAdvocates: form.extraAdvocates.filter((_, i) => i !== idx) });

  const handleUpdateExtraAdvocate = (idx, key, value) => {
    const list = form.extraAdvocates.map((a, i) => (i === idx ? { ...a, [key]: value } : a));
    update({ extraAdvocates: list });
  };

  const handleAddAddress = () =>
    update({ partyAddresses: [...form.partyAddresses, { name: "", address: "" }] });

  const handleRemoveAddress = (idx) => {
    if (form.partyAddresses.length === 1) {
      update({ partyAddresses: [{ name: "", address: "" }] });
    } else {
      update({ partyAddresses: form.partyAddresses.filter((_, i) => i !== idx) });
    }
  };

  const handleUpdateAddress = (idx, key, value) => {
    const list = form.partyAddresses.map((p, i) =>
      i === idx ? { ...p, [key]: value } : p
    );
    update({ partyAddresses: list });
  };

  const handleReset = () => {
    setForm(emptyForm);
    toast.success("Form has been reset.");
  };

  const handleGenerate = () => {
    if (!validate()) return;
    toast.success("Documents are ready. Use the preview or download buttons.", {
      description: "Three documents generated: Vakalatnama · Adjournment · Address Memo",
    });
  };

  const fileSafe = useMemo(() => {
    const base = (form.caseNo || "Case").replace(/[^a-zA-Z0-9-_]+/g, "_");
    return base.slice(0, 40);
  }, [form.caseNo]);

  // ============= Payment-gated PDF download =============
  // Every download entry point routes through PaymentDownloadDialog. The
  // actual PDF generation runs ONLY after successful backend verification.
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [paymentInitial, setPaymentInitial] = useState([]);

  const openPaymentForDoc = (which) => {
    if (!validate()) return;
    setPaymentInitial([which]);
    setPaymentOpen(true);
  };

  const openPaymentForAll = () => {
    if (!validate()) return;
    setPaymentInitial(["vakalatnama", "adjournment", "addressMemo"]);
    setPaymentOpen(true);
  };

  const generatePdfFor = async (which, lang, suffix) => {
    let el = null;
    let name = "";
    if (which === "vakalatnama") {
      el = lang === "mr" ? vakMrRef.current : vakRef.current;
      name = `Vakalatnama${suffix}_${fileSafe}.pdf`;
    } else if (which === "adjournment") {
      el = lang === "mr" ? adjMrRef.current : adjRef.current;
      name = `Adjournment${suffix}_${fileSafe}.pdf`;
    } else {
      el = lang === "mr" ? memoMrRef.current : memoRef.current;
      name = `AddressMemo${suffix}_${fileSafe}.pdf`;
    }
    await generatePdfFromElement(el, name);
    return name;
  };

  const performDownloads = async (docKeys) => {
    if (!docKeys || docKeys.length === 0) return;
    if (marathiCtx?.editMode) marathiCtx.setEditMode(false);
    setGenerating(true);
    try {
      const lang = previewLang;
      const suffix = lang === "mr" ? "_MR" : "";
      for (const which of docKeys) {
        await generatePdfFor(which, lang, suffix);
      }
      toast.success(
        docKeys.length === 1
          ? "Download started"
          : `All ${docKeys.length} PDFs downloaded.`
      );
    } catch (e) {
      console.error(e);
      toast.error("Could not generate one or more PDFs.");
    } finally {
      setGenerating(false);
    }
  };

  const downloadOne = async (which) => {
    openPaymentForDoc(which);
  };

  const downloadAll = async () => {
    openPaymentForAll();
  };

  const printOne = (which) => {
    if (!validate()) return;
    const lang = previewLang;
    const map = lang === "mr"
      ? { vakalatnama: vakMrRef, adjournment: adjMrRef, addressMemo: memoMrRef }
      : { vakalatnama: vakRef, adjournment: adjRef, addressMemo: memoRef };
    printElements([map[which].current]);
  };

  const printAll = () => {
    if (!validate()) return;
    const lang = previewLang;
    const els = lang === "mr"
      ? [vakMrRef.current, adjMrRef.current, memoMrRef.current]
      : [vakRef.current, adjRef.current, memoRef.current];
    printElements(els);
  };

  const previewProps = {
    vakalatnama: {
      title: previewLang === "mr" ? "वकील पत्र — Preview" : "Vakalatnama — Preview",
      child: previewLang === "mr" ? <VakalatnamaMarathi data={form} /> : <Vakalatnama data={form} />,
    },
    adjournment: {
      title: previewLang === "mr" ? "तहकुबी अर्ज — Preview" : "Adjournment Application — Preview",
      child: previewLang === "mr" ? <AdjournmentApplicationMarathi data={form} /> : <AdjournmentApplication data={form} />,
    },
    addressMemo: {
      title: previewLang === "mr" ? "पत्ता मेमो — Preview" : "Address Memo — Preview",
      child: previewLang === "mr" ? <AddressMemoMarathi data={form} /> : <AddressMemo data={form} />,
    },
  };

  return (
    <div className="min-h-screen" data-testid="legal-form-page">
      {/* Header */}
      <header className="bg-[#1c1a17] text-stone-100 border-b border-stone-800">
        <div className="max-w-6xl mx-auto px-6 py-6 flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-3">
            <div className="bg-[#8a6e3b] p-2.5 rounded-md">
              <Scale className="h-6 w-6 text-white" />
            </div>
            <div>
              <h1
                className="text-xl font-bold tracking-wide"
                style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
                data-testid="app-title"
              >
                Advocate Document Studio
              </h1>
              <p className="text-xs text-stone-400 tracking-wider uppercase">
                Vakalatnama · Adjournment · Address Memo
              </p>
            </div>
          </div>
          <div className="text-xs text-stone-400 italic flex items-center gap-3">
            {user ? (
              <>
                <button
                  onClick={() => { window.location.hash = "#/"; }}
                  className="text-stone-300 hover:text-white flex items-center gap-1"
                  data-testid="btn-back-home-civil"
                >
                  ← Home
                </button>
                <span className="text-stone-300 not-italic" data-testid="user-email">
                  {user.email}
                </span>
                <button
                  onClick={logout}
                  className="text-stone-300 hover:text-white flex items-center gap-1"
                  data-testid="btn-signout"
                >
                  <LogOut className="h-3 w-3" /> Sign out
                </button>
              </>
            ) : (
              <span>English-only · A4 Print-ready</span>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* LEFT: Form */}
          <div className="lg:col-span-2 space-y-6">
            {/* CASE DETAILS */}
            <Card data-testid="card-case-details">
              <CardHeader className="border-b">
                <CardTitle className="section-heading">Case Details</CardTitle>
                <CardDescription>Court and case identification information.</CardDescription>
              </CardHeader>
              <CardContent className="pt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="Case Number" required>
                  <Input
                    value={form.caseNo}
                    onChange={(e) => update({ caseNo: e.target.value })}
                    placeholder="e.g. CR. No. 245/2025"
                    data-testid="input-case-no"
                  />
                </Field>
                <Field label="Date" required>
                  <Input
                    type="date"
                    value={form.date}
                    onChange={(e) => update({ date: e.target.value })}
                    data-testid="input-date"
                  />
                </Field>
                <Field label="Court Name" required>
                  <Input
                    value={form.courtName}
                    onChange={(e) => update({ courtName: e.target.value })}
                    placeholder="e.g. Hon'ble Civil Judge Sr. Div."
                    data-testid="input-court-name"
                  />
                </Field>
                <Field label="Court Place" required>
                  <Input
                    value={form.courtPlace}
                    onChange={(e) => update({ courtPlace: e.target.value })}
                    placeholder="e.g. Pune"
                    data-testid="input-court-place"
                  />
                </Field>
              </CardContent>
            </Card>

            {/* PARTY DETAILS */}
            <Card data-testid="card-party-details">
              <CardHeader className="border-b">
                <CardTitle className="section-heading">Party Details</CardTitle>
                <CardDescription>
                  Names and roles of both parties. Address fields appear at the bottom of the
                  form.
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-6 space-y-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Field label="Party No. 1 Name" required>
                    <Input
                      value={form.party1Name}
                      onChange={(e) => update({ party1Name: e.target.value })}
                      placeholder="Full legal name"
                      data-testid="input-party1-name"
                    />
                  </Field>
                  <Field label="Party No. 1 Role">
                    <Select
                      value={form.party1Role}
                      onValueChange={(v) => update({ party1Role: v })}
                    >
                      <SelectTrigger data-testid="select-party1-role">
                        <SelectValue placeholder="Select role" />
                      </SelectTrigger>
                      <SelectContent>
                        {PARTY1_ROLES.map((r) => (
                          <SelectItem key={r} value={r}>
                            {r}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Field label="Party No. 2 Name" required>
                    <Input
                      value={form.party2Name}
                      onChange={(e) => update({ party2Name: e.target.value })}
                      placeholder="Full legal name"
                      data-testid="input-party2-name"
                    />
                  </Field>
                  <Field label="Party No. 2 Role">
                    <Input
                      value={form.party2Role || ""}
                      readOnly
                      disabled
                      placeholder="Auto-filled from Party No. 1"
                      data-testid="input-party2-role"
                    />
                  </Field>
                </div>
              </CardContent>
            </Card>

            {/* ADVOCATE APPEARING FOR */}
            <Card data-testid="card-advocate-appearing">
              <CardHeader className="border-b">
                <CardTitle className="section-heading">Advocate Appearing For</CardTitle>
                <CardDescription>
                  Which party / side the advocate represents. Drives wording in every
                  document.
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-6 grid grid-cols-1 sm:grid-cols-[2fr_1fr] gap-4">
                <Field label="Advocate Represents" required>
                  <Select
                    value={form.advocateAppearingFor}
                    onValueChange={(v) => update({ advocateAppearingFor: v })}
                  >
                    <SelectTrigger data-testid="select-appearing-for">
                      <SelectValue placeholder="Select role" />
                    </SelectTrigger>
                    <SelectContent>
                      {ROLES.map((r) => (
                        <SelectItem key={r} value={r}>
                          {r}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Party Number(s)" hint="e.g. 1 · 1, 2, 3 · 1 to 5 · 1, 3 to 6">
                  <Input
                    type="text"
                    value={form.advocateAppearingNumber}
                    onChange={(e) => update({ advocateAppearingNumber: e.target.value })}
                    placeholder="e.g. 1, 2, 3 or 1 to 5"
                    data-testid="input-appearing-number"
                  />
                </Field>
              </CardContent>
            </Card>

            {/* MAIN ADVOCATE */}
            <Card data-testid="card-main-advocate">
              <CardHeader className="border-b">
                <CardTitle className="section-heading">Main Advocate Details</CardTitle>
                <CardDescription>Contact details printed on every document.</CardDescription>
              </CardHeader>
              <CardContent className="pt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="Advocate Name" required>
                  <Input
                    value={form.advocateName}
                    onChange={(e) => update({ advocateName: e.target.value })}
                    placeholder="Adv. Full Name"
                    data-testid="input-adv-name"
                  />
                </Field>
                <Field label="MAH Number" required hint="Bar council enrollment, e.g. 2222/2002">
                  <Input
                    value={form.advocateMahNumber}
                    onChange={(e) => update({ advocateMahNumber: e.target.value })}
                    placeholder="e.g. 2222/2002"
                    data-testid="input-adv-mah"
                  />
                </Field>
                <Field label="Advocate Code" required hint="District / sanad code, e.g. 203">
                  <Input
                    value={form.advocateCode}
                    onChange={(e) => update({ advocateCode: e.target.value })}
                    placeholder="e.g. 203"
                    data-testid="input-adv-code"
                  />
                </Field>
                <div className="sm:col-span-1"></div>
                <div className="sm:col-span-2">
                  <Field label="Office Address" required>
                    <Textarea
                      value={form.advocateAddress}
                      onChange={(e) => update({ advocateAddress: e.target.value })}
                      placeholder="Office address, area, city, pincode"
                      rows={2}
                      data-testid="input-adv-address"
                    />
                  </Field>
                </div>
                <Field label="Mobile Number" required>
                  <Input
                    value={form.advocateMobile}
                    onChange={(e) => update({ advocateMobile: e.target.value })}
                    placeholder="+91 9XXXXXXXXX"
                    data-testid="input-adv-mobile"
                  />
                </Field>
                <Field label="Email Address" required>
                  <Input
                    type="email"
                    value={form.advocateEmail}
                    onChange={(e) => update({ advocateEmail: e.target.value })}
                    placeholder="advocate@example.com"
                    data-testid="input-adv-email"
                  />
                </Field>
              </CardContent>
            </Card>

            {/* EXTRA ADVOCATES */}
            <Card data-testid="card-extra-advocates">
              <CardHeader className="border-b flex flex-row items-start justify-between gap-3">
                <div>
                  <CardTitle className="section-heading">Associate Advocates</CardTitle>
                  <CardDescription>
                    Optional. Each associate has Name, MAH Number and Advocate Code.
                  </CardDescription>
                </div>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={handleAddExtraAdvocate}
                  className="gap-1.5"
                  data-testid="btn-add-extra-advocate"
                >
                  <Plus className="h-4 w-4" /> Add Extra Advocate
                </Button>
              </CardHeader>
              <CardContent className="pt-6 space-y-5">
                {form.extraAdvocates.length === 0 ? (
                  <p className="text-sm text-muted-foreground italic">
                    No additional advocates added.
                  </p>
                ) : (
                  form.extraAdvocates.map((a, idx) => (
                    <div
                      key={idx}
                      className="rounded-md border border-stone-200 bg-stone-50/50 p-4 space-y-3"
                      data-testid={`extra-adv-row-${idx}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="text-xs font-semibold uppercase tracking-wider text-stone-600">
                          Associate Advocate #{idx + 1}
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50 h-7 w-7"
                          onClick={() => handleRemoveExtraAdvocate(idx)}
                          data-testid={`btn-remove-extra-adv-${idx}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <Field label="Advocate Name">
                          <Input
                            value={a.name}
                            onChange={(e) =>
                              handleUpdateExtraAdvocate(idx, "name", e.target.value)
                            }
                            placeholder="Adv. Name"
                            data-testid={`input-extra-adv-name-${idx}`}
                          />
                        </Field>
                        <Field label="MAH Number">
                          <Input
                            value={a.mah}
                            onChange={(e) =>
                              handleUpdateExtraAdvocate(idx, "mah", e.target.value)
                            }
                            placeholder="e.g. 9999/2021"
                            data-testid={`input-extra-adv-mah-${idx}`}
                          />
                        </Field>
                        <Field label="Advocate Code">
                          <Input
                            value={a.code}
                            onChange={(e) =>
                              handleUpdateExtraAdvocate(idx, "code", e.target.value)
                            }
                            placeholder="e.g. 412"
                            data-testid={`input-extra-adv-code-${idx}`}
                          />
                        </Field>
                      </div>
                    </div>
                  ))
                )}
              </CardContent>
            </Card>

            {/* PARTY ADDRESS DETAILS */}
            <Card data-testid="card-party-addresses">
              <CardHeader className="border-b flex flex-row items-start justify-between gap-3">
                <div>
                  <CardTitle className="section-heading">Party Address Details</CardTitle>
                  <CardDescription>
                    Used in the Address Memo&apos;s two-column table — column&nbsp;1
                    &ldquo;Name and Address&rdquo; (combined), column&nbsp;2
                    &ldquo;Signature&rdquo; (left blank for manual sign).
                  </CardDescription>
                </div>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={handleAddAddress}
                  className="gap-1.5"
                  data-testid="btn-add-address"
                >
                  <Plus className="h-4 w-4" /> Add Entry
                </Button>
              </CardHeader>
              <CardContent className="pt-6 space-y-4">
                {form.partyAddresses.map((p, idx) => (
                  <div
                    key={idx}
                    className="grid grid-cols-1 sm:grid-cols-[1fr_1.6fr_auto] gap-3 items-start"
                    data-testid={`address-row-${idx}`}
                  >
                    <Field label={`Name #${idx + 1}`}>
                      <Input
                        value={p.name}
                        onChange={(e) => handleUpdateAddress(idx, "name", e.target.value)}
                        placeholder="e.g. Ramesh Sharma"
                        data-testid={`input-address-name-${idx}`}
                      />
                    </Field>
                    <Field label="Address">
                      <Textarea
                        value={p.address}
                        onChange={(e) => handleUpdateAddress(idx, "address", e.target.value)}
                        placeholder="R/o. Plot No., Street, Area, City — Pincode, State"
                        rows={3}
                        data-testid={`input-address-value-${idx}`}
                      />
                    </Field>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="text-red-600 hover:text-red-700 hover:bg-red-50 mt-7"
                      onClick={() => handleRemoveAddress(idx)}
                      data-testid={`btn-remove-address-${idx}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* ADJOURNMENT REASON */}
            <Card data-testid="card-misc">
              <CardHeader className="border-b">
                <CardTitle className="section-heading">Adjournment Note</CardTitle>
                <CardDescription>
                  Optional reason — used only in the Adjournment Application. If blank,
                  the standard wording from the template is used.
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-6">
                <Field label="Adjournment Reason (optional)">
                  <Input
                    value={form.adjournmentReason}
                    onChange={(e) => update({ adjournmentReason: e.target.value })}
                    placeholder="e.g. necessary documents not yet received"
                    data-testid="input-adjournment-reason"
                  />
                </Field>
              </CardContent>
            </Card>
          </div>

          {/* RIGHT: Actions */}
          <aside className="lg:sticky lg:top-6 self-start">
            <Card className="border-stone-300" data-testid="card-actions">
              <CardHeader className="border-b brand-accent">
                <CardTitle className="text-white text-base">Actions</CardTitle>
                <CardDescription className="text-stone-200">
                  Generate, preview, print & download.
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-5 space-y-4">
                {/* Language toggle */}
                <div className="rounded-md border border-stone-200 bg-stone-50 p-2 flex items-center gap-1" data-testid="lang-toggle">
                  <Languages className="h-4 w-4 text-stone-500 mr-1" />
                  <button
                    className={`flex-1 text-xs font-semibold py-1.5 rounded transition-colors ${
                      previewLang === "en"
                        ? "bg-[#8a6e3b] text-white"
                        : "text-stone-600 hover:bg-stone-100"
                    }`}
                    onClick={() => setPreviewLang("en")}
                    data-testid="btn-lang-en"
                  >
                    English
                  </button>
                  <button
                    className={`flex-1 text-xs font-semibold py-1.5 rounded transition-colors ${
                      previewLang === "mr"
                        ? "bg-[#8a6e3b] text-white"
                        : "text-stone-600 hover:bg-stone-100"
                    }`}
                    onClick={() => setPreviewLang("mr")}
                    data-testid="btn-lang-mr"
                  >
                    मराठी
                  </button>
                </div>

                <Button
                  className="w-full gap-2 bg-[#8a6e3b] hover:bg-[#6b5226] text-white"
                  onClick={handleGenerate}
                  data-testid="btn-generate"
                >
                  <FileText className="h-4 w-4" /> Generate Documents
                </Button>

                <Separator />

                <DocAction
                  label="Vakalatnama"
                  onPreview={() => setPreview("vakalatnama")}
                  onDownload={() => downloadOne("vakalatnama")}
                  onPrint={() => printOne("vakalatnama")}
                  generating={generating}
                  testIdBase="vakalatnama"
                />
                <DocAction
                  label="Adjournment Application"
                  onPreview={() => setPreview("adjournment")}
                  onDownload={() => downloadOne("adjournment")}
                  onPrint={() => printOne("adjournment")}
                  generating={generating}
                  testIdBase="adjournment"
                />
                <DocAction
                  label="Address Memo"
                  onPreview={() => setPreview("addressMemo")}
                  onDownload={() => downloadOne("addressMemo")}
                  onPrint={() => printOne("addressMemo")}
                  generating={generating}
                  testIdBase="address-memo"
                />

                <Separator />

                <Button
                  variant="secondary"
                  className="w-full gap-2"
                  onClick={downloadAll}
                  disabled={generating}
                  data-testid="btn-download-all"
                >
                  <PackageOpen className="h-4 w-4" />
                  {generating ? "Preparing…" : "Download All PDFs"}
                </Button>
                <Button
                  variant="outline"
                  className="w-full gap-2"
                  onClick={printAll}
                  data-testid="btn-print-all"
                >
                  <Printer className="h-4 w-4" /> Print All Documents
                </Button>
                <Button
                  variant="ghost"
                  className="w-full gap-2 text-red-600 hover:text-red-700 hover:bg-red-50"
                  onClick={handleReset}
                  data-testid="btn-reset"
                >
                  <RotateCcw className="h-4 w-4" /> Reset Form
                </Button>

                <Separator />

                <Button
                  variant="outline"
                  className="w-full gap-2"
                  onClick={handleSaveProfile}
                  data-testid="btn-save-profile"
                >
                  <Save className="h-4 w-4" /> Save Advocate Profile
                </Button>
                <Button
                  variant="outline"
                  className="w-full gap-2"
                  onClick={handleSaveCase}
                  data-testid="btn-save-case"
                >
                  <Save className="h-4 w-4" />
                  {currentCaseId ? "Update Case" : "Save Case"}
                </Button>
                <Button
                  variant="ghost"
                  className="w-full gap-2 justify-start"
                  onClick={() => { setShowCases((v) => !v); loadCases(); }}
                  data-testid="btn-toggle-cases"
                >
                  <FolderOpen className="h-4 w-4" />
                  My Cases ({savedCases.length})
                </Button>
                {showCases && (
                  <div className="rounded border border-stone-200 max-h-64 overflow-auto text-sm" data-testid="cases-list">
                    {savedCases.length === 0 ? (
                      <div className="p-3 text-stone-500 italic text-xs">No saved cases yet.</div>
                    ) : (
                      savedCases.map((c) => (
                        <div
                          key={c.id}
                          className={`px-3 py-2 border-b border-stone-100 last:border-0 hover:bg-stone-50 cursor-pointer flex items-center justify-between gap-2 ${
                            currentCaseId === c.id ? "bg-stone-100" : ""
                          }`}
                          onClick={() => handleLoadCase(c.id)}
                          data-testid={`case-item-${c.id}`}
                        >
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-stone-800 truncate">{c.title}</div>
                            <div className="text-[10px] text-stone-500">
                              {new Date(c.updated_at).toLocaleString()}
                            </div>
                          </div>
                          <button
                            onClick={(e) => handleDeleteCase(c.id, e)}
                            className="text-red-500 hover:text-red-700"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
            <p className="text-xs text-muted-foreground italic mt-3 px-1">
              Tip: All processing happens in your browser. Nothing is uploaded or stored.
            </p>
          </aside>
        </div>
      </main>

      {/* ====== Off-screen render host for PDF/print ====== */}
      <div className="pdf-render-host print-area" aria-hidden="true">
        <Vakalatnama ref={vakRef} data={form} />
        <AdjournmentApplication ref={adjRef} data={form} />
        <AddressMemo ref={memoRef} data={form} />
        <VakalatnamaMarathi ref={vakMrRef} data={form} />
        <AdjournmentApplicationMarathi ref={adjMrRef} data={form} />
        <AddressMemoMarathi ref={memoMrRef} data={form} />
      </div>

      {/* ====== Payment-gated download dialog ====== */}
      <PaymentDownloadDialog
        open={paymentOpen}
        onOpenChange={setPaymentOpen}
        initialSelected={paymentInitial}
        onDownload={performDownloads}
        userEmail={user?.email || ""}
        userName={user?.name || ""}
      />

      {/* ====== Preview Dialogs ====== */}
      {preview && (
        <DocumentPreviewDialog
          open={!!preview}
          onOpenChange={(v) => (!v ? setPreview(null) : null)}
          title={previewProps[preview].title}
          onDownload={() => downloadOne(preview)}
          testId={`preview-${preview}`}
          isMarathi={previewLang === "mr"}
        >
          {previewProps[preview].child}
        </DocumentPreviewDialog>
      )}

      <footer className="border-t bg-stone-50 py-6 mt-12">
        <div className="max-w-6xl mx-auto px-6 text-center text-xs text-stone-500">
          Advocate Document Studio · For legal professional drafting use only · Built for
          A4 printing.
        </div>
      </footer>
    </div>
  );
}

/* ===== Helpers ===== */

function Field({ label, required, hint, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-semibold text-stone-700 tracking-wide">
        {label}
        {required ? <span className="text-red-600 ml-0.5">*</span> : null}
      </Label>
      {children}
      {hint ? <p className="text-[11px] text-stone-500 italic">{hint}</p> : null}
    </div>
  );
}

function DocAction({ label, onPreview, onDownload, onPrint, generating, testIdBase }) {
  return (
    <div className="space-y-2">
      <p className="text-xs font-semibold text-stone-600 uppercase tracking-wider">
        {label}
      </p>
      <div className="grid grid-cols-3 gap-1.5">
        <Button
          size="sm"
          variant="outline"
          className="gap-1"
          onClick={onPreview}
          data-testid={`btn-${testIdBase}-preview`}
        >
          <Eye className="h-3.5 w-3.5" /> Preview
        </Button>
        <Button
          size="sm"
          variant="outline"
          className="gap-1"
          onClick={onDownload}
          disabled={generating}
          data-testid={`btn-${testIdBase}-download`}
        >
          <Download className="h-3.5 w-3.5" /> PDF
        </Button>
        <Button
          size="sm"
          variant="outline"
          className="gap-1"
          onClick={onPrint}
          data-testid={`btn-${testIdBase}-print`}
        >
          <Printer className="h-3.5 w-3.5" /> Print
        </Button>
      </div>
    </div>
  );
}

/* Print helper — opens a fresh window with the selected elements only. */
function printElements(elements) {
  const printWin = window.open("", "_blank", "width=900,height=1000");
  if (!printWin) return;
  const styles = Array.from(document.styleSheets)
    .map((sheet) => {
      try {
        return Array.from(sheet.cssRules)
          .map((r) => r.cssText)
          .join("\n");
      } catch {
        return "";
      }
    })
    .join("\n");

  printWin.document.write(`
    <!doctype html>
    <html>
      <head>
        <meta charset="utf-8" />
        <title>Print — Legal Documents</title>
        <style>
          ${styles}
          body { margin:0; padding:0; background:#fff; }
          .a4-sheet { box-shadow:none !important; margin:0 auto !important; page-break-after: always; }
          .a4-sheet:last-child { page-break-after:auto; }
          @page { size: A4; margin: 0; }
        </style>
      </head>
      <body>${elements.map((el) => (el ? el.outerHTML : "")).join("")}</body>
    </html>
  `);
  printWin.document.close();
  printWin.focus();
  setTimeout(() => {
    printWin.print();
    printWin.close();
  }, 500);
}
